import { inject, Injectable, Injector } from '@angular/core';
import { ClientPlatformType, CONFIG_TOKEN, LoginState } from '@pxl/models/common';
import { LoginCredentials, LoginService, PlatformService, StorageService, TermsOfUseService } from '@pxl/models/contracts';
import { AccountRoutingRouteNames, AppRoutingRouteNames } from '@pxl/route-names';
import {
    AccountService as AccountBackendService,
    CredentialsModel,
    LoginResultModel,
    MatchEmailLinkAndUserResultModel,
    RegistrationVerificationResultModel,
} from '@pxl/shared/generated';
import { GuidUtil } from '@pxl/shared/util';
import { firstValueFrom, from, merge, Observable, Subject } from 'rxjs';

type SignInDataToPersist = {
    token?: string;
    isTermsOfUseAccepted?: boolean;
};

@Injectable()
export class DefaultLoginService implements LoginService {
    private readonly storageTokenKey = 'token';
    private readonly storageTermsAcceptedKey = 'termsAccepted';
    private readonly storageClientIdKey = 'clientId';
    private readonly loginChange$ = new Subject<LoginState>();
    private readonly onBeforeUserLogout$ = new Subject<string>();
    private token: string = null;

    private readonly accountBackendService = inject(AccountBackendService);
    private readonly storageService = inject(StorageService);
    private readonly platformService = inject(PlatformService);
    private readonly config = inject(CONFIG_TOKEN);
    // Lazily resolved via Injector: DefaultTermsOfUseService injects LoginService, so an eager
    // field inject(TermsOfUseService) here would create a DI cycle.
    private readonly injector = inject(Injector);

    public onBeforeUserLogout(): Observable<string> {
        return this.onBeforeUserLogout$.asObservable();
    }

    public onUserLoginChanged(): Observable<LoginState> {
        return merge(from(this.initialLoginState()), this.loginChange$);
    }

    public async loginUser(credentials: LoginCredentials): Promise<LoginResultModel> {
        const clientId = await this.getClientId();
        const loginCredentials: CredentialsModel = {
            email: credentials.email,
            password: credentials.password,
            clientId,
            whiteLabelId: this.config.whitelabelId,
        };

        if (credentials.multiFactorAuthentication) {
            loginCredentials.multiFactorAuthentication = credentials.multiFactorAuthentication;
        }

        // A valid recovery code makes the API remove the multi factor authentication instead of verifying it.
        if (credentials.multiFactorAuthenticationRecoveryCode) {
            loginCredentials.multiFactorAuthenticationRecoveryCode = credentials.multiFactorAuthenticationRecoveryCode;
        }

        const loginResult = await firstValueFrom(this.accountBackendService.login(loginCredentials, this.config.API_VERSION));

        if (loginResult.token) {
            const { token, termsOfUseForUserValid } = loginResult;
            await this.setSignInData({ token, isTermsOfUseAccepted: termsOfUseForUserValid });
            this.loginChange$.next(new LoginState(true, true, false, termsOfUseForUserValid));
        }

        return loginResult;
    }

    public async loginUserWithToken(token: string): Promise<void> {
        await this.setSignInData({ token });

        const isTermsOfUseForUserValid = await this.fetchIsTermsOfUseForUserValid();
        await this.setSignInData({ token, isTermsOfUseAccepted: isTermsOfUseForUserValid });
        this.loginChange$.next(new LoginState(true, false, false, isTermsOfUseForUserValid));
    }

    public async termsOfUseAccepted(): Promise<void> {
        await this.setSignInData({ token: this.token, isTermsOfUseAccepted: true });
        this.loginChange$.next(new LoginState(true, false, false, true));
    }

    public async logoutUser(userInteraction: boolean, accessDenied: boolean): Promise<void> {
        this.onBeforeUserLogout$.next(this.token);

        try {
            await this.removeAndInvalidateToken();
            await this.removeAcceptedTermsOfUse();
        } finally {
            const state = new LoginState(false, userInteraction, accessDenied, false);

            this.loginChange$.next(state);
            this.token = null;

            if (state.isBasedOnUserInteraction) {
                location.replace(`#/${AppRoutingRouteNames.SignIn}/${AccountRoutingRouteNames.Login}`);
            } else if (state.accessDenied) {
                location.replace(`#/${AppRoutingRouteNames.UnAuthorized}`);
            } else {
                location.reload();
            }
        }
    }

    public async isLoggedIn(): Promise<boolean> {
        if (this.token) {
            return true;
        }
        this.token = await this.getTokenFromStorage();
        return !!this.token;
    }

    public async isTermsOfUseAccepted(): Promise<boolean> {
        return Boolean(await this.storageService.get(this.storageTermsAcceptedKey));
    }

    public async getClientInfos(): Promise<{
        clientId: string;
        platformId: ClientPlatformType;
        appVersion: string;
    }> {
        const clientId = await this.getClientId();
        const platformId = this.platformService.isNativePlatform()
            ? ClientPlatformType.PlatformCordova
            : ClientPlatformType.PlatformBrowser;

        const appVersion = this.config.APP_VERSION;

        return {
            clientId,
            platformId,
            appVersion,
        };
    }

    public async getClientId(): Promise<string> {
        const storageClientId = await this.storageService.get(this.storageClientIdKey);

        if (!storageClientId) {
            return this.provideClientId();
        }

        return storageClientId as string;
    }

    public async verifyLinkAndLoginUser(linkId: string): Promise<RegistrationVerificationResultModel> {
        const clientId = await this.getClientId();
        return this.accountsRegisterVerification(linkId, clientId);
    }

    public matchEmailLinkAndUser(emailLinkId: string): Promise<MatchEmailLinkAndUserResultModel> {
        return firstValueFrom(this.accountBackendService.matchEmailLinkAndUser({ emailLinkId }, this.config.API_VERSION, this.token));
    }

    public getToken(): string | null {
        return this.token || null;
    }

    public async removeAndInvalidateToken(): Promise<void> {
        // We first try to remove the token from the persistent storage and after that we try to make a web request and invalidate the token.
        if (!(await this.isLoggedIn())) {
            return;
        }

        const currentToken = this.token;

        await Promise.all([
            // Remove local token.
            this.removeToken(),
            // Remove remote token.
            // passing null will cause the current logged-in user making this http request to be logged out.
            firstValueFrom(this.accountBackendService.removeToken({ tokenId: null }, this.config.API_VERSION, currentToken)),
        ]);
    }

    private async initialLoginState(): Promise<LoginState> {
        const isLoggedIn = await this.isLoggedIn();
        const isTermsOfUseForUserValid = isLoggedIn ? await this.fetchIsTermsOfUseForUserValid() : false;
        return new LoginState(isLoggedIn, false, false, isTermsOfUseForUserValid);
    }

    // Mirrors accepted-tos.guard.ts: valid = isEulaValid && isPrivacyPolicyValid.
    // Errors and timeouts close the gate (Invariante 3) and never block boot.
    private async fetchIsTermsOfUseForUserValid(): Promise<boolean> {
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        try {
            const termsOfUse = await Promise.race([
                this.injector.get(TermsOfUseService).getUserTermsOfUse(),
                new Promise<never>((_, reject) => {
                    timeoutId = setTimeout(() => reject(new Error('terms-of-use check timed out')), 10000);
                }),
            ]);
            return Boolean(termsOfUse.isEulaValid && termsOfUse.isPrivacyPolicyValid);
        } catch {
            return false;
        } finally {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        }
    }

    private async accountsRegisterVerification(linkId: string, clientId: string): Promise<RegistrationVerificationResultModel> {
        const registrationVerificationResultModel = await firstValueFrom(
            this.accountBackendService.finishRegistration(
                {
                    linkId,
                    clientId,
                },
                this.config.API_VERSION,
            ),
        );

        if (registrationVerificationResultModel.token) {
            await this.loginUserWithToken(registrationVerificationResultModel.token);
        }

        return registrationVerificationResultModel;
    }

    private async provideClientId(): Promise<string> {
        const storageClientId = GuidUtil.GenerateGuid();

        await this.storageService.set(this.storageClientIdKey, storageClientId);

        return storageClientId;
    }

    // Token
    private async setSignInData({ token, isTermsOfUseAccepted }: SignInDataToPersist): Promise<void> {
        if (token) {
            await this.storageService.set(this.storageTokenKey, token);
        }
        if (isTermsOfUseAccepted !== undefined) {
            await this.storageService.set(this.storageTermsAcceptedKey, isTermsOfUseAccepted);
        }
        this.token = token;
    }

    private async removeToken(): Promise<void> {
        const storedToken = await this.storageService.get(this.storageTokenKey);

        // If the user has legitimately logged in, in a different browser tab, we don't want to kick him out.
        if (this.token === storedToken) {
            await this.storageService.remove(this.storageTokenKey);
        }

        this.token = null;
    }

    private async removeAcceptedTermsOfUse(): Promise<void> {
        await this.storageService.remove(this.storageTermsAcceptedKey);
    }

    private async getTokenFromStorage(): Promise<string> {
        if (!this.storageService.getStorage()) await this.storageService.init();
        return (await this.storageService.get(this.storageTokenKey)) as Promise<string>;
    }
}
